import type Phaser from "phaser";
import { coneAlpha, coversStanding, markCoverage, propArtHeight, propArtMeetsCone, propArtOverlap, propArtWithin, propCoverage, propFades, propFeet, type AssetManifest, type FrameMaskOf, type FrameRect, type OccluderRule, type PixelBox } from "daedalus-core";
import type { ConeSight, CoveringPiece, KeptCover, PieceAlpha } from "./prop-pieces.ts";

/**
 * The occlusion queries of a `PropView` (ADR-040, ADR-061's amendments for P1.9-T75 and P1.9-T82, ADR-071
 * decisions 9 and 10), over the view's live props: the occluder fade and the cone of sight's ease, the shares of a
 * rectangle the props hide, the pieces that cover a character and the alphas the fade leaves. The view owns the
 * props, the pool and the frames; this reads them, and writes only the alphas, `cover`, `dim` and `shade`.
 */

/**
 * A live prop. `id` is the prop's id; `image` is its one image, the whole frame (ADR-132 decision 1). `frame` is the
 * prop's own frame, which positions, sorts and fades it; `shown` is the frame on screen, which differs while
 * `showFrame` plays another over it. `cover` is the alpha the occluder fade leaves it at, `dim` the alpha the cone of
 * sight has eased it to and `shade` that alpha at the cone's strength (`coneAlpha`); it draws at the lower of `cover`
 * and `shade`.
 */
export interface LiveProp {
  readonly id: string;
  readonly image: Phaser.GameObjects.Image;
  x: number;
  y: number;
  frame: string;
  shown: string;
  animated: boolean;
  flipX: boolean;
  flipY: boolean;
  selected: boolean;
  cover: number;
  dim: number;
  shade: number;
}

/**
 * The occlusion queries over one view's live props.
 */
export class PropOcclusion {
  private dimmedCount = 0;

  /**
   * @param live The view's live props, read in place, so the queries always see the props as they are.
   * @param manifest Asset manifest.
   * @param tileSize Tile size in pixels.
   * @param coneDims Whether a cone of sight may dim the view's props (`PropView`'s `coneDims`).
   * @param changed Called once for each change to what the view draws over a character, which moves the view's version.
   */
  public constructor(
    private readonly live: ReadonlyMap<string, LiveProp>,
    private readonly manifest: AssetManifest,
    private readonly tileSize: number,
    private readonly coneDims: boolean,
    private readonly changed: () => void,
  ) {}

  /**
   * How many props the cone of sight held below full alpha at the last fade, for the report.
   *
   * @returns The count.
   */
  public get dimmed(): number {
    return this.dimmedCount;
  }

  /**
   * Fades every prop that hides most of a rectangle while sorting in front of it, and restores the rest,
   * so a character standing behind a tree still shows through while one beside a fence or a sign does not
   * make it fade (ADR-040). A prop hides the rectangle when its art rises at least `rule.minHeight` pixels
   * above its base and its solid pixels, mirrored with its flips, cover at least `rule.minCoverage` of it.
   * The cheap tests run first: the depth, the height, then the share of the rectangle inside the art bounds,
   * which no count can exceed, so a frame's pixels are read and counted only for a prop that could pass. A
   * prop is judged by its own frame while `showFrame` shows another. A prop the caller exempts is restored
   * and never tested: the editor exempts its presence props, which the game draws in views of their own that
   * are never faded (ADR-058 decision 6, ADR-071 decision 10). A prop whose frame the asset manifest marks `noFade`
   * (`propFades`, the game's choice at the asset build; ADR-040's amendment of 2026-10-03) is treated the same on
   * every page: neither this fade nor the cone dims it, so it stays whole and `mostCovered` still counts it.
   *
   * With a cone of sight (ADR-061's amendment for P1.9-T75), the same walk eases each prop's cone alpha: a shown,
   * non-ground prop not exempt, whose art rises at least `sight.rule.minHeight` above its base and whose art bounds,
   * mirrored with it, meet the cone (`propArtMeetsCone`), at any depth, eases toward `sight.rule.alpha`, and every other prop back toward 1,
   * by `sight.elapsedMs` over `sight.rule.fadeMs` of the whole way. Without one every cone alpha returns to 1 at once.
   * A prop draws at the lower of its occluder alpha and its cone alpha at the sight's strength (`coneAlpha`, the
   * amendment for P1.9-T82). A sight at no strength holds no prop and tests none, and a view the cone may not dim
   * (`coneDims` false) is never in it.
   *
   * @param box Rectangle in world pixels, `x0, y0` top-left, `x1, y1` bottom-right inclusive, whole pixels.
   * @param depth Depth of what stands behind; props at or below it are not occluders.
   * @param rule The least height and share for a prop to fade, and its alpha when it does.
   * @param maskOf The solid pixels of a frame, by frame id.
   * @param exempt Whether a prop, by id, is never faded; absent, none is.
   * @param sight The cone of sight to fade props in as well; absent, none.
   * @returns How many props are faded over the rectangle now.
   * @throws RangeError when the rectangle is empty or off whole pixels and a prop reaches the share test.
   */
  public fadeOccluders(box: PixelBox, depth: number, rule: OccluderRule, maskOf: FrameMaskOf, exempt?: (id: string) => boolean, sight?: ConeSight): number {
    let faded = 0;
    let dimmed = 0;
    for (const entry of this.live.values()) {
      const rect = this.manifest.frames[entry.frame];
      const image = entry.image;
      const free = exempt?.(entry.id) !== true && (rect === undefined || propFades(rect));
      const occludes = rect !== undefined && free && propArtHeight(rect) >= rule.minHeight && this.bound(entry, rect, depth, box) >= rule.minCoverage && this.judge(entry, depth, box, maskOf) >= rule.minCoverage;
      const cover = occludes ? rule.alpha : 1;
      if (entry.cover !== cover) {
        entry.cover = cover;
        this.changed();
      }
      if ((sight !== undefined && sight.strength > 0 && this.coneDims) || entry.dim !== 1) {
        const seen = sight !== undefined && sight.strength > 0 && this.coneDims && free && rect !== undefined && image.visible && rect.ground !== true && propArtHeight(rect) >= sight.rule.minHeight && propArtMeetsCone(entry, rect, this.tileSize, sight.cone);
        this.easeDim(entry, seen ? (sight as ConeSight).rule.alpha : 1, sight);
      }
      const shade = entry.dim === 1 ? 1 : coneAlpha(entry.dim, sight === undefined ? 1 : sight.strength);
      if ((entry.shade === 1) !== (shade === 1)) {
        this.changed();
      }
      entry.shade = shade;
      this.fadeImage(entry, entry.cover, shade);
      faded += occludes ? 1 : 0;
      dimmed += shade < 1 ? 1 : 0;
    }
    this.dimmedCount = dimmed;
    return faded;
  }

  /**
   * The cheap upper bound of what `judge` finds: the share of a box inside a prop's art when the prop sorts in front
   * of `depth`, which no count of pixels can exceed. Read-only, and allocates nothing.
   *
   * @param entry The live prop.
   * @param rect Its frame.
   * @param depth Depth of what stands behind; a prop at or below it is not counted.
   * @param box The box in world pixels, whole pixels, every edge inclusive.
   * @returns A share in `[0, 1]`, 0 when the prop is not in front.
   * @throws RangeError when the box is empty or off whole pixels and the prop is in front.
   */
  private bound(entry: LiveProp, rect: FrameRect, depth: number, box: PixelBox): number {
    return entry.image.depth > depth ? propArtOverlap(entry, rect, this.tileSize, box) : 0;
  }

  /**
   * The share of a box a prop hides while sorting in front of it, by all the solid pixels of its frame: the one place
   * the view decides how much of a body a prop covers, and so whether it fades, counts toward the most-covered share
   * and marks pixels as kept. Read-only, and allocates nothing.
   *
   * @param entry The live prop.
   * @param depth Depth of what stands behind; a prop at or below it is not counted.
   * @param box The box in world pixels, whole pixels, every edge inclusive.
   * @param maskOf The solid pixels of a frame, by frame id.
   * @returns A share in `[0, 1]`.
   * @throws RangeError when the box is empty or off whole pixels and the prop is in front.
   */
  private judge(entry: LiveProp, depth: number, box: PixelBox, maskOf: FrameMaskOf): number {
    return entry.image.depth > depth ? propCoverage(entry, maskOf(entry.frame), this.tileSize, box) : 0;
  }

  /**
   * Gives a prop's image the lower of its occluder alpha and its cone alpha.
   *
   * @param entry The live prop.
   * @param cover The prop's occluder alpha.
   * @param shade The prop's cone alpha at the cone's strength.
   */
  private fadeImage(entry: LiveProp, cover: number, shade: number): void {
    const alpha = Math.min(cover, shade);
    if (entry.image.alpha !== alpha) {
      entry.image.setAlpha(alpha);
    }
  }

  /**
   * Steps a prop's cone alpha toward a target, linearly, by the sight's elapsed time over its fade time for the whole
   * way from 1 to the cone alpha; without a sight, or with no fade time, it lands at once.
   *
   * @param entry The live prop.
   * @param target The cone alpha it eases toward.
   * @param sight The cone of sight, or none.
   */
  private easeDim(entry: LiveProp, target: number, sight: ConeSight | undefined): void {
    const from = entry.dim;
    if (from === target) {
      return;
    }
    let next = target;
    if (sight !== undefined && sight.rule.fadeMs > 0) {
      const step = (sight.elapsedMs * (1 - sight.rule.alpha)) / sight.rule.fadeMs;
      next = from < target ? Math.min(target, from + step) : Math.max(target, from - step);
    }
    entry.dim = next;
  }

  /**
   * The largest share of a rectangle one shown, whole prop hides while sorting in front of it: the occluder fade's
   * measure (`propCoverage`) asked of each prop the fade could fade, without fading anything, so a caller can tell
   * how hidden another character is. A prop takes part when its image is visible and drawn at full alpha (a faded
   * prop is see-through), its depth is above `depth` and its art rises at least `minHeight` pixels above its base. The
   * cheap tests run first, and a prop whose art bounds hold no more of the rectangle than the best share so far is
   * never counted, so pixels are read only for a prop that could raise the answer. Read-only, and allocates nothing.
   * With `kept`, the same walk raises `kept.share` to the largest share one prop a cone of sight never dims hides: a
   * `noFade` prop, and any prop of a view the cone passes over (`coneDims` false; ADR-061's amendment for P1.9-T82),
   * counting such a prop whenever its art bounds hold more of the rectangle than `kept.share`.
   *
   * @param box Rectangle in world pixels, `x0, y0` top-left, `x1, y1` bottom-right inclusive, whole pixels.
   * @param depth Depth of what stands behind; props at or below it are not counted.
   * @param minHeight The least height in pixels a prop's art must rise above its base to count.
   * @param maskOf The solid pixels of a frame, by frame id.
   * @param from The share to beat, for a caller folding several views; 0 for the first.
   * @param kept The share props the cone never dims give, raised in place; absent, it is not measured.
   * @returns The largest share in `[0, 1]`, or `from` when no prop beats it.
   * @throws RangeError when the rectangle is empty or off whole pixels and a prop reaches the share test.
   */
  public mostCovered(box: PixelBox, depth: number, minHeight: number, maskOf: FrameMaskOf, from = 0, kept?: KeptCover): number {
    let best = from;
    for (const entry of this.live.values()) {
      const rect = this.manifest.frames[entry.frame];
      if (rect === undefined || !entry.image.visible || entry.image.alpha !== 1 || propArtHeight(rect) < minHeight) {
        continue;
      }
      const overlap = this.bound(entry, rect, depth, box);
      const keeps = kept !== undefined && (!propFades(rect) || !this.coneDims) && overlap > kept.share;
      if (overlap <= best && !keeps) {
        continue;
      }
      const share = this.judge(entry, depth, box, maskOf);
      best = Math.max(best, share);
      if (kept !== undefined && keeps && share > kept.share) {
        kept.share = share;
      }
    }
    return best;
  }

  /**
   * The area, in square pixels, of the art of the props a cone of sight could dim that lies inside a rectangle
   * (ADR-061's amendment for P1.9-T82, `propArtWithin`): each shown, non-ground prop whose frame fades (`propFades`)
   * and whose art rises at least `minHeight` above its base, at any depth, overlaps counted once a prop. Summed over
   * the views and divided by the rectangle's area it is the cover a game gates its cone by. A view the cone may not
   * dim (`coneDims` false) answers 0. Read-only, and allocates nothing.
   *
   * @param left The rectangle's left edge in world pixels.
   * @param top Its top edge.
   * @param right Its right edge.
   * @param bottom Its bottom edge.
   * @param minHeight The least height in pixels a prop's art must rise above its base to count.
   * @returns The summed area.
   */
  public coneCover(left: number, top: number, right: number, bottom: number, minHeight: number): number {
    if (!this.coneDims) {
      return 0;
    }
    let area = 0;
    for (const entry of this.live.values()) {
      const rect = this.manifest.frames[entry.frame];
      if (rect === undefined || !entry.image.visible || rect.ground === true || !propFades(rect) || propArtHeight(rect) < minHeight) {
        continue;
      }
      area += propArtWithin(entry, rect, this.tileSize, left, top, right, bottom);
    }
    return area;
  }

  /**
   * Marks the pixels of a rectangle that the view's props that never fade hide (`FrameRect.noFade`) while sorting in
   * front of it, into bytes the caller keeps (`markCoverage`): marked over every view, they give the share of a body
   * such props hide together, a wall, a door and a porch each hiding part of it, where `mostCovered` answers for one
   * prop alone. A prop takes part when its image is visible, its depth is above `depth` and its art bounds hold some of
   * the rectangle, whatever its height. Read-only on the props, and allocates nothing.
   *
   * @param box Rectangle in world pixels, `x0, y0` top-left, `x1, y1` bottom-right inclusive, whole pixels.
   * @param depth Depth of what stands behind; props at or below it are not counted.
   * @param maskOf The solid pixels of a frame, by frame id.
   * @param marks One byte per pixel of the rectangle, row-major, 1 where a prop already hides it; written in place.
   * @returns How many pixels the view newly marked.
   * @throws RangeError when the rectangle is empty or off whole pixels, or `marks` is smaller than it, and a prop
   *   reaches the pixels.
   */
  public markKept(box: PixelBox, depth: number, maskOf: FrameMaskOf, marks: Uint8Array): number {
    let marked = 0;
    for (const entry of this.live.values()) {
      const rect = this.manifest.frames[entry.frame];
      if (rect === undefined || propFades(rect) || !entry.image.visible || entry.image.depth <= depth || propArtOverlap(entry, rect, this.tileSize, box) <= 0) {
        continue;
      }
      marked += markCoverage(entry, maskOf(entry.frame), this.tileSize, box, marks);
    }
    return marked;
  }

  /**
   * Undoes the occluder fade: every prop draws at its cone alpha at the cone's strength again, which is full alpha
   * unless the cone of sight holds it (ADR-061's amendment for P1.9-T75), for a character that is no longer there or a fade run at another
   * point for a report.
   */
  public restoreAlpha(): void {
    for (const entry of this.live.values()) {
      if (entry.cover !== 1) {
        entry.cover = 1;
        this.changed();
      }
      this.fadeImage(entry, 1, entry.shade);
    }
  }

  /**
   * The shown props that cover a character (`coversStanding`): each judged by its own frame, which positions
   * and sorts it, whatever `showFrame` shows over it, and by the depth its image draws at. Read-only.
   *
   * @param body The character's body box in world pixels, whole pixels, every edge inclusive.
   * @param standing The character's depth.
   * @param maskOf The solid pixels of a frame, by frame id.
   * @returns The covering pieces.
   * @throws RangeError when the box is empty or off whole pixels and a prop draws above the character.
   */
  public covering(body: PixelBox, standing: number, maskOf: FrameMaskOf): CoveringPiece[] {
    const pieces: CoveringPiece[] = [];
    for (const entry of this.live.values()) {
      const rect = this.manifest.frames[entry.frame];
      if (rect === undefined || !entry.image.visible || !coversStanding(entry, maskOf(entry.frame), entry.image.depth, this.tileSize, body, standing)) {
        continue;
      }
      pieces.push(this.pieceOf(entry, rect.w));
    }
    return pieces;
  }

  /**
   * Every shown prop with the alpha the occluder fade leaves it at, the cone of sight aside (ADR-061's amendment for
   * P1.9-T75), each named by its own frame. Read-only, for comparing what the occluder fade leaves on two pages, one
   * of which, the editor's, has no cone.
   *
   * @returns The pieces and their alphas.
   */
  public alphas(): PieceAlpha[] {
    const pieces: PieceAlpha[] = [];
    for (const entry of this.live.values()) {
      const rect = this.manifest.frames[entry.frame];
      if (rect !== undefined && entry.image.visible) {
        pieces.push({ ...this.pieceOf(entry, rect.w), alpha: entry.cover });
      }
    }
    return pieces;
  }

  /**
   * A live prop as a piece: its own frame, its frame's bottom-left and its feet point, in world pixels.
   *
   * @param entry The live prop.
   * @param frameWidth Its frame's width in pixels.
   * @returns The piece.
   */
  private pieceOf(entry: LiveProp, frameWidth: number): CoveringPiece {
    const feet = propFeet(entry, frameWidth, this.tileSize);
    return { frame: entry.frame, left: entry.x * this.tileSize, bottom: (entry.y + 1) * this.tileSize, feetX: feet.x * this.tileSize, feetY: feet.y * this.tileSize };
  }
}
